import React from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, FileText, RefreshCcw, Lock, Info, ArrowLeft } from "lucide-react";
import Header, { TopBar } from "@/components/Header";
import Footer from "@/components/Footer";
import { CONTACT } from "@/mock";

const C = CONTACT.company;

const CONTENT = {
  about: {
    icon: Info,
    title: "About Us",
    intro: `${C} is a full-service online travel agency built to make travel simple, affordable and stress-free for every Indian traveller.`,
    sections: [
      { h: "Who we are", p: [`${C} (brand: FLYTRIP) is an IATA-aligned travel company offering flights, hotels, holiday packages, bus and visa assistance through a single platform. We combine live airline & hotel inventory with human 24x7 support.`] },
      { h: "What we do", p: ["Domestic & international flight booking with live fares.", "Handpicked hotels and curated holiday packages.", "Group booking, cab, train and visa enquiry assistance.", "Dedicated B2B agent portal with wallet & instant ticketing."] },
      { h: "Our promise", p: ["Transparent pricing with no hidden fees, real-time fares straight from airlines, and support that actually picks up the phone. If a ticket can't be issued, your money is auto-refunded — you never lose funds."] },
    ],
  },
  terms: {
    icon: FileText,
    title: "Terms & Conditions",
    intro: `By using ${C} (FLYTRIP) you agree to the following terms. Please read them carefully before booking.`,
    sections: [
      { h: "1. Bookings & Pricing", p: ["All fares and availability are live and can change until your booking is confirmed and ticketed. The final price shown at payment is binding for that transaction.", "You are responsible for entering correct passenger names (as per government ID), dates and contact details. Airlines may deny boarding for name mismatches."] },
      { h: "2. Payments", p: ["Payments are processed via our secure PCI-DSS compliant gateway (Razorpay). We never store your full card details.", "A convenience fee may apply and is shown before payment."] },
      { h: "3. Cancellations & Changes", p: ["Cancellation, refund and date-change charges are governed by the airline/hotel fare rules plus our service fee. See our Cancellation and Refund policies.", "Some promotional/non-refundable fares carry no refund except statutory taxes."] },
      { h: "4. Use of the Platform", p: ["You agree not to misuse the platform, scrape content, or make speculative/fraudulent bookings. We may cancel such bookings.", "Content, logos and data on this site are owned by us or our suppliers and may not be reproduced without permission."] },
      { h: "5. Liability", p: [`${C} acts as an intermediary between you and travel suppliers (airlines, hotels, etc.). We are not liable for supplier delays, cancellations, schedule changes or service quality, though we assist you in every way possible.`] },
      { h: "6. Governing Law", p: ["These terms are governed by the laws of India. Disputes are subject to the jurisdiction of courts at our registered office location."] },
    ],
  },
  privacy: {
    icon: Lock,
    title: "Privacy Policy",
    intro: `${C} respects your privacy. This policy explains what we collect and how we use it.`,
    sections: [
      { h: "Information we collect", p: ["Contact details (name, email, phone), traveller details required for bookings, and payment references (never full card numbers).", "Usage data such as searches and device information to improve our service."] },
      { h: "How we use it", p: ["To process and manage your bookings, issue tickets, send confirmations and provide support.", "To send offers and updates (you can opt out anytime).", "To comply with legal, tax and regulatory obligations."] },
      { h: "Sharing", p: ["We share only the data necessary to fulfil your booking with airlines, hotels and payment processors. We do not sell your personal data."] },
      { h: "Security & your rights", p: ["Data is transmitted over encrypted (HTTPS) connections and stored securely. You may request access, correction or deletion of your data by emailing " + CONTACT.email + "."] },
    ],
  },
  "payment-security": {
    icon: ShieldCheck,
    title: "Payment Security",
    intro: "Your payments are 100% secure. Here's how we protect every transaction.",
    sections: [
      { h: "PCI-DSS compliant gateway", p: ["All payments are processed through Razorpay, a PCI-DSS Level 1 certified payment gateway. Your card/UPI/netbanking details are entered on the gateway, never on our servers."] },
      { h: "Encryption", p: ["Every page and API call is served over TLS/HTTPS encryption. Sensitive data is never stored in plain text."] },
      { h: "Fraud protection & auto-refund", p: ["Bookings are validated with the supplier before your money moves. If a ticket cannot be issued after payment, the full amount is automatically refunded to your source — you never lose money."] },
      { h: "Supported methods", p: ["UPI, Credit/Debit Cards (VISA, Mastercard, RuPay, AMEX), Net Banking, and wallets (Paytm, PhonePe, MobiKwik, BHIM, G Pay)."] },
    ],
  },
  cancellation: {
    icon: RefreshCcw,
    title: "Cancellation Policy",
    intro: "You can cancel eligible bookings anytime. Charges depend on the airline/hotel fare rules.",
    sections: [
      { h: "How to cancel", p: ["Log in and open My Bookings, or contact our 24x7 support at " + CONTACT.phonePretty + " / " + CONTACT.email + ". We'll process the cancellation and initiate any eligible refund."] },
      { h: "Charges", p: ["Cancellation charges = airline/hotel penalty (as per your fare rules) + applicable convenience/service fee.", "Charges increase as you get closer to departure/check-in.", "Non-refundable and some promo fares are not eligible for a fare refund — only statutory taxes may be returned."] },
      { h: "No-shows", p: ["Tickets not cancelled before departure (no-show) are generally non-refundable except for statutory taxes, subject to airline policy."] },
    ],
  },
  refund: {
    icon: RefreshCcw,
    title: "Refund Policy",
    intro: "Eligible refunds are processed quickly and transparently to your original payment method.",
    sections: [
      { h: "Refund timelines", p: ["Once a cancellation is confirmed, refunds are initiated to your original payment method (Razorpay) and typically reflect in 5–7 working days, depending on your bank.", "Airline/hotel-side refunds may take longer as they are subject to the supplier releasing funds."] },
      { h: "Refund amount", p: ["Refund = amount paid − airline/hotel cancellation penalty − applicable service/convenience fee. The exact break-up is shared at the time of cancellation."] },
      { h: "Failed bookings", p: ["If your payment succeeds but the ticket cannot be issued, we auto-refund the full amount — no request needed."] },
      { h: "Need help?", p: ["For any refund query, contact " + CONTACT.email + " or call " + CONTACT.phonePretty + " with your PNR/Booking ID."] },
    ],
  },
};

const LegalPage = ({ page }) => {
  const navigate = useNavigate();
  const data = CONTENT[page] || CONTENT.about;
  const Icon = data.icon;
  return (
    <div className="min-h-screen bg-[#F5F7FB]" data-testid={`legal-page-${page}`}>
      <TopBar />
      <Header />
      <section className="relative overflow-hidden bg-gradient-to-b from-[#0A0F2E] to-[#1B1F5B] py-12">
        <div className="mx-auto max-w-4xl px-4">
          <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1.5 text-sm font-semibold text-white/70 hover:text-white" data-testid="legal-back">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F5B32B]"><Icon className="h-6 w-6 text-[#0A0F2E]" /></span>
            <h1 className="font-[Poppins] text-3xl font-extrabold text-white sm:text-4xl">{data.title}</h1>
          </div>
          <p className="mt-3 max-w-2xl text-sm text-white/70 md:text-base">{data.intro}</p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-10">
        <div className="space-y-6 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm md:p-8">
          {data.sections.map((s, i) => (
            <div key={i}>
              <h2 className="text-lg font-extrabold text-slate-800">{s.h}</h2>
              <div className="mt-2 space-y-2">
                {s.p.map((para, j) => (
                  <p key={j} className="text-sm leading-relaxed text-slate-600">{para}</p>
                ))}
              </div>
            </div>
          ))}
          <p className="border-t border-slate-100 pt-4 text-xs text-slate-400">
            Last updated: {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })} · For questions email {CONTACT.email}
          </p>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default LegalPage;
